import React, { useEffect, useState } from "react";
import { ukDayTime as fmtDT } from "@/lib/time";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plug, CheckCircle2, XCircle, Send, Trash2, ShieldCheck, GitBranch } from "lucide-react";

const PROVIDERS = [
  { key: "hubspot", label: "HubSpot", blurb: "Private App token — leads land as a Contact + Deal", cred: "Private App access token", hint: "Settings → Integrations → Private Apps" },
  { key: "salesforce", label: "Salesforce", blurb: "Web-to-Lead Org ID — leads land as Salesforce Leads", cred: "Org ID (starts 00D…)", hint: "Setup → Web-to-Lead" },
  { key: "pipedrive", label: "Pipedrive", blurb: "API token + domain — leads land as a Person + Deal", cred: "Personal API token", hint: "Personal preferences → Personal API" },
];


function MappingSection({ cfg, enabled, onSaved }) {
  const [pipelines, setPipelines] = useState(null);
  const [err, setErr] = useState("");
  const [pipelineId, setPipelineId] = useState(cfg.pipeline_id || "");
  const [stageId, setStageId] = useState(cfg.stage_id || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/lender/crm/pipelines")
      .then((r) => setPipelines(r.data.supported ? r.data.pipelines : []))
      .catch((e) => { setPipelines([]); setErr(e?.response?.data?.detail || "Could not load pipelines from your CRM"); });
  }, []);

  const pipeline = (pipelines || []).find((p) => p.id === pipelineId);

  const save = async () => {
    if (!pipelineId || !stageId) { toast.error("Pick both a pipeline and a stage"); return; }
    setSaving(true);
    try {
      const stage = pipeline?.stages.find((s) => s.id === stageId);
      await api.put("/lender/crm", {
        provider: cfg.provider, enabled,
        pipeline_id: pipelineId, stage_id: stageId,
        pipeline_label: pipeline?.label, stage_label: stage?.label,
      });
      toast.success("Deal mapping saved — new Connexis deals will land there");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="crm-mapping-section">
      <div className="flex items-center gap-1.5 text-[10.5px] font-semibold tracking-[0.18em] uppercase text-slate-600">
        <GitBranch className="size-3.5 text-[#0055FF]" /> Where should deals land?
      </div>
      {cfg.pipeline_label && cfg.stage_label && (
        <div className="mt-1.5 text-[12px] text-slate-500" data-testid="crm-mapping-current">
          Currently: <b className="text-slate-700">{cfg.pipeline_label}</b> → <b className="text-slate-700">{cfg.stage_label}</b>
        </div>
      )}
      {err ? (
        <div className="mt-2 text-[12px] text-[#0044CC]" data-testid="crm-mapping-error">{err}</div>
      ) : !pipelines ? (
        <div className="mt-3 flex items-center gap-2 text-[12px] text-slate-400"><Loader2 className="size-3.5 animate-spin" /> Loading your pipelines…</div>
      ) : (
        <div className="mt-3 flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">Pipeline</Label>
            <Select value={pipelineId} onValueChange={(v) => { setPipelineId(v); setStageId(""); }}>
              <SelectTrigger className="mt-1 w-[210px] bg-white" data-testid="crm-pipeline-select"><SelectValue placeholder="Choose pipeline" /></SelectTrigger>
              <SelectContent>
                {pipelines.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`crm-pipeline-opt-${p.id}`}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">Stage</Label>
            <Select value={stageId} onValueChange={setStageId} disabled={!pipeline}>
              <SelectTrigger className="mt-1 w-[210px] bg-white" data-testid="crm-stage-select"><SelectValue placeholder={pipeline ? "Choose stage" : "Pick a pipeline first"} /></SelectTrigger>
              <SelectContent>
                {(pipeline?.stages || []).map((s) => <SelectItem key={s.id} value={s.id} data-testid={`crm-stage-opt-${s.id}`}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={save} disabled={saving} variant="outline" className="rounded-xl" data-testid="crm-mapping-save">
            {saving ? <Loader2 className="size-4 animate-spin" /> : "Save mapping"}
          </Button>
          <div className="text-[11px] text-slate-400 max-w-[220px] leading-snug">Leave unset and deals land in your CRM&apos;s default pipeline &amp; first stage.</div>
        </div>
      )}
    </div>
  );
}

export function CrmCard() {
  const [data, setData] = useState(null);
  const [provider, setProvider] = useState("hubspot");
  const [token, setToken] = useState("");
  const [domain, setDomain] = useState("");
  const [orgId, setOrgId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/lender/crm");
      setData(r.data);
      if (r.data.config) {
        setProvider(r.data.config.provider);
        setEnabled(r.data.config.enabled);
        setDomain(r.data.config.domain || "");
      }
    } catch { setData({ config: null, deliveries: [] }); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy("save");
    try {
      const body = { provider, enabled };
      if (provider === "salesforce") body.org_id = orgId;
      else { body.token = token; if (provider === "pipedrive") body.domain = domain; }
      await api.put("/lender/crm", body);
      toast.success("CRM connected — routed leads will now flow straight in");
      setToken(""); setOrgId("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not connect — check the credential"); }
    finally { setBusy(null); }
  };

  const test = async () => {
    setBusy("test");
    try {
      await api.post("/lender/crm/test");
      toast.success("Test lead delivered — check your CRM for 'Connexis Test Lead'");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test delivery failed"); }
    finally { setBusy(null); }
  };

  const disconnect = async () => {
    setBusy("disconnect");
    try { await api.delete("/lender/crm"); toast.success("CRM disconnected"); setToken(""); setOrgId(""); setDomain(""); load(); }
    catch { toast.error("Disconnect failed"); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="size-5 animate-spin text-slate-400" /></div>;
  const cfg = data.config;
  const meta = PROVIDERS.find((p) => p.key === provider);
  const needsCred = provider === "salesforce" ? !orgId.trim() : (provider === "pipedrive" ? !(token.trim() && domain.trim()) : !token.trim());
  const canSave = cfg?.provider === provider ? false : needsCred; // existing provider can re-save toggles without re-pasting

  return (
    <div className="space-y-4" data-testid="lender-crm-card">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-[10.5px] font-semibold tracking-[0.22em] uppercase text-slate-600">
              <Plug className="size-3.5 text-[#0055FF]" /> CRM delivery
            </div>
            <p className="mt-1.5 text-[13px] text-slate-500 max-w-lg">
              Connect your CRM and every lead routed to you lands there instantly — no copy-paste. Your credential is stored securely and never shown again.
            </p>
          </div>
          {cfg && (
            <div className={`inline-flex items-center gap-1.5 rounded-full border px-3 h-7 text-[11.5px] font-semibold ${cfg.enabled ? "border-teal-200 bg-teal-50 text-teal-700" : "border-slate-200 bg-slate-50 text-slate-500"}`} data-testid="crm-status-chip">
              <ShieldCheck className="size-3" /> {cfg.label} · {cfg.credential_hint || "connected"} {cfg.enabled ? "" : "· paused"}
            </div>
          )}
        </div>

        {/* Provider presets */}
        <div className="mt-4 grid sm:grid-cols-3 gap-2.5">
          {PROVIDERS.map((p) => (
            <button key={p.key} onClick={() => setProvider(p.key)}
                    className={`text-left rounded-xl border p-3.5 transition-colors ${provider === p.key ? "border-[#0055FF] bg-[#0055FF]/[0.04] ring-1 ring-[#0055FF]/30" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`crm-provider-${p.key}`}>
              <div className="text-[13.5px] font-semibold text-slate-900">{p.label}</div>
              <div className="mt-0.5 text-[11.5px] text-slate-500 leading-snug">{p.blurb}</div>
            </button>
          ))}
        </div>

        {/* Credential fields */}
        <div className="mt-4 grid sm:grid-cols-2 gap-3 items-end">
          {provider === "salesforce" ? (
            <div>
              <Label className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600">{meta.cred}</Label>
              <Input value={orgId} onChange={(e) => setOrgId(e.target.value)} placeholder="00D..." className="mt-1.5" data-testid="crm-orgid-input" />
              <div className="mt-1 text-[11px] text-slate-400">{meta.hint}</div>
            </div>
          ) : (
            <div>
              <Label className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600">{meta.cred}</Label>
              <Input type="password" value={token} onChange={(e) => setToken(e.target.value)}
                     placeholder={cfg?.provider === provider ? `Saved (${cfg.credential_hint}) — paste to replace` : "Paste token…"}
                     className="mt-1.5" data-testid="crm-token-input" />
              <div className="mt-1 text-[11px] text-slate-400">{meta.hint}</div>
            </div>
          )}
          {provider === "pipedrive" && (
            <div>
              <Label className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600">Company domain</Label>
              <div className="mt-1.5 flex items-center gap-1.5">
                <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="yourcompany" className="max-w-[200px]" data-testid="crm-domain-input" />
                <span className="text-[12px] text-slate-400">.pipedrive.com</span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <Button onClick={save} disabled={busy === "save" || canSave} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-xl" data-testid="crm-save">
            {busy === "save" ? <Loader2 className="size-4 animate-spin" /> : "Save & connect"}
          </Button>
          {cfg && (
            <>
              <Button onClick={test} disabled={busy === "test"} variant="outline" className="rounded-xl" data-testid="crm-test">
                {busy === "test" ? <Loader2 className="size-4 animate-spin" /> : <><Send className="size-3.5 mr-1.5" /> Send test lead</>}
              </Button>
              <div className="inline-flex items-center gap-2 text-[12.5px] text-slate-600">
                <Switch checked={enabled} onCheckedChange={(v) => setEnabled(v)} data-testid="crm-enabled-switch" />
                Deliver new leads automatically
              </div>
              <Button onClick={disconnect} disabled={busy === "disconnect"} variant="ghost" className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto" data-testid="crm-disconnect">
                <Trash2 className="size-3.5 mr-1.5" /> Disconnect
              </Button>
            </>
          )}
        </div>

        {cfg && cfg.provider === provider && (
          provider === "salesforce" ? (
            <div className="mt-4 text-[11.5px] text-slate-400" data-testid="crm-mapping-na">
              Salesforce Web-to-Lead always lands new records in your Leads queue — pipeline mapping doesn&apos;t apply.
            </div>
          ) : (
            <MappingSection key={cfg.provider} cfg={cfg} enabled={enabled} onSaved={load} />
          )
        )}
      </div>

      {/* Delivery log */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 text-[10.5px] font-semibold tracking-[0.22em] uppercase text-slate-600">Delivery log</div>
        {(data.deliveries || []).length === 0 ? (
          <div className="py-10 text-center text-[13px] text-slate-400" data-testid="crm-deliveries-empty">No deliveries yet — connect a CRM and send a test lead.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="crm-deliveries-table">
            <thead>
              <tr className="text-left bg-slate-50 text-slate-400">
                <th className="px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em]">When</th>
                <th className="px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em]">Lead</th>
                <th className="px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em]">CRM</th>
                <th className="px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em]">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.deliveries.map((d) => (
                <tr key={d.id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmtDT(d.created_at)}</td>
                  <td className="px-4 py-2.5">
                    <span className="font-semibold text-slate-900">{d.lead_label}</span>
                    {d.amount_gbp > 0 && <span className="ml-1.5 text-slate-400">£{Math.round(d.amount_gbp).toLocaleString()}</span>}
                    {d.source === "test" && <span className="ml-1.5 inline-flex rounded-full border border-slate-200 bg-slate-50 text-slate-500 px-1.5 h-4 text-[9.5px] font-semibold items-center">TEST</span>}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600 capitalize">{d.provider}</td>
                  <td className="px-4 py-2.5">
                    {d.ok ? (
                      <span className="inline-flex items-center gap-1 text-teal-600 font-semibold text-[12px]"><CheckCircle2 className="size-3.5" /> Delivered</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#0044CC] font-semibold text-[12px]" title={d.detail?.error}><XCircle className="size-3.5" /> Failed</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
